import { useEffect, useState } from "react";
import { get } from "../Libraries/Ajax/HttpService";
import { useTheme } from "../context/ThemeContext";
import { ChevronDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { useDistrict } from "../utils/DistrictContext";
import { Spin } from "antd";
export default function PredictiveAnalytics({
  predictiveSystem,
  setPredictiveSystem,
}) {
  const { theme, colors } = useTheme();
    const { selectedDistrict } = useDistrict();
  const [flowPredictionData, setFlowPredictionData] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [loading, setLoading] = useState(false);  // Added loading state

  const mapSystemToApi = (system) => {
    if (selectedDistrict === "FB151") return "total";
    if (system === "Total") return "total";
    if (system === "Non-Potable") return "non_potable";
    if (system === "Potable") return "potable";
    return "total";
  };

  useEffect(() => {
    const fetchPredictionData = async () => {
      setLoading(true);  // Show loader when fetching data

      try {
        const systemKey = mapSystemToApi(predictiveSystem);

        // Fetch Flow Prediction Data
        const res = await get(`apis/prediction_${systemKey}`);
        const combined = res?.data?.combined?.data || [];

        const formatted = combined.map((item) => ({
          week: item.week,
          actual: item.actualvalue && Number(item.actualvalue) !== 0 ? Number(item.actualvalue) : null,
          previousPrediction: item.pre_prediction && Number(item.pre_prediction) !== 0 ? Number(item.pre_prediction) : null,
          prediction2026: item.predictedvalue && Number(item.predictedvalue) !== 0 ? Number(item.predictedvalue) : null,
        }));

        setFlowPredictionData(formatted);

        // Fetch Forecast Data
        const resNext = await get(`apis/prediction_${systemKey}_next`);
        const combinedNext = resNext?.data?.combined?.data || [];
        const currentLength = resNext?.data?.current_data?.length || 0;

        const formattedNext = combinedNext.map((item, index) => {
          const flowValue = item.Flow && Number(item.Flow) !== 0 ? Number(item.Flow) : null;
          return {
            week: item.week,
            actualFlow: index < currentLength ? flowValue : null,
            predictedFlow: index >= currentLength - 1 ? flowValue : null,
          };
        });

        setForecastData(formattedNext);

      } catch (err) {
        console.error("Prediction API error:", err);
      } finally {
        setLoading(false);  // Hide loader after data is fetched
      }
    };

    fetchPredictionData();
 }, [predictiveSystem, selectedDistrict]);

  /* Tooltip */
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        className="rounded-lg shadow-lg px-4 py-3 border"
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.background,
          borderColor: `${colors.primary}30`,
          boxShadow: `0 8px 32px ${colors.primary}30`,
          backdropFilter: "blur(6px)",
        }}
      >
        <p className="text-xs mb-2" style={{ color: colors.textSecondary }}>
          {label}
        </p>

        {payload.map(
          (entry, index) =>
            entry.value !== null && (
              <p
                key={index}
                className="text-sm font-semibold"
                style={{ color: entry.color }}
              >
                {entry.name}: {Number(entry.value).toLocaleString()}
              </p>
            ),
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Dropdown */}
        {selectedDistrict !== "FB151" && (
      <div className="flex justify-end items-center">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold" style={{ color: colors.text }}>
            System
          </span>
          <div className="relative">
            <select
              value={predictiveSystem}
              onChange={(e) => setPredictiveSystem(e.target.value)}
              className="w-full px-3 py-2 pr-8 border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none transition-all"
              style={{
                background: colors.background,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              <option value="Total" style={{
                backgroundColor: theme === "white" ? "#ffffff" : colors.background,
                color: colors.text,
              }}>Total</option>
              <option value="Non-Potable" style={{
                backgroundColor: theme === "white" ? "#ffffff" : colors.background,
                color: colors.text,
              }}>Non-Potable</option>
              <option value="Potable" style={{
                backgroundColor: theme === "white" ? "#ffffff" : colors.background,
                color: colors.text,
              }}>Potable</option>
            </select>

            <ChevronDown
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{
                color: colors.textSecondary,
              }}
            />
          </div>
        </div>
      </div>
     )}
      {/* Flow Prediction – 2026 */}
      <div
        className="border rounded-lg p-6 max-[767px]:overflow-x-scroll"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <h3
          className="text-lg font-bold mb-4"
          style={{ color: colors.primary }}
        >
          Flow Prediction - 2026
        </h3>
        <div className="max-[767px]:w-[600px]">
          {/* Show loader while fetching data */}
          {loading ? (
            <div className="text-center text-xl text-gray-500">
              <Spin size="large" />
              <span className="mt-2 block text-sm" style={{color: colors.textSecondary}}> Fetching the data...</span>
            </div>
          ) : flowPredictionData.length === 0 ? (
            <div className="text-center text-xl text-gray-500">No Data Available</div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={flowPredictionData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis
                  dataKey="week"
                  angle={-40}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 10, fill: colors.textSecondary }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: colors.textSecondary }}
                  tickFormatter={(value) => value.toLocaleString("en-US")}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ fontSize: "12px", paddingBottom: "20px" }}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Actual Value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="previousPrediction"
                  name="Previous Prediction"
                  stroke="#9ca3af"
                  strokeDasharray="3 3"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="prediction2026"
                  name="Prediction 2026"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* 12-Month Forecast */}
      <div
        className="border rounded-lg p-6 max-[767px]:overflow-x-scroll mb-5"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <h3
          className="text-lg font-bold mb-4"
          style={{ color: colors.primary }}
        >
          12-Month Forecast
        </h3>
        <div className="max-[767px]:w-[600px]">
          {/* Show loader while fetching data */}
          {loading ? (
           <div className="text-center text-xl text-gray-500">
              <Spin size="large" />
              <span className="mt-2 block text-sm" style={{color: colors.textSecondary}}> Fetching the data...</span>
            </div>
          ) : forecastData.length === 0 ? (
            <div className="text-center text-xl text-gray-500">No Data Available</div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis
                  dataKey="week"
                  angle={-40}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 10, fill: colors.textSecondary }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: colors.textSecondary }}
                  tickFormatter={(value) => value.toLocaleString("en-US")}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ fontSize: "12px", paddingBottom: "20px" }}
                />
                <Line
                  type="monotone"
                  dataKey="actualFlow"
                  name="Actual Flow"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="predictedFlow"
                  name="Predicted Flow"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}